import {
  Button, Center, Checkbox,
  Container,
  FormControl,
  FormLabel, Heading, HStack, Input, Radio, RadioGroup,
  Spinner, Text,
} from "@chakra-ui/react";
import {AxiosResponse} from "axios";
import { Field, Form, Formik } from "formik";
import React, {useState} from "react";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {Navigate, useLocation, useNavigate} from "react-router-dom";

import {getCurrentUser as apiGetCurrentUser, submitUserFormD} from "../Api";
import {
  useAuthentication,
} from "../hooks/AuthenticationProvider";
import { disableBlurProtection } from "../hooks/BlurProtection";
import {Paths} from "../model/Constants";
import {UserDto} from "../model/Dto";
import {authenticationKeys} from "../QueryKeys";
import {enableKeyCommands, isInAvataiSite} from "../Utils";

type FormParams = {
  privado: string;
  sexo: string;
  region: string;
  primeraLengua: string;
  clasesExtra: string;
  email: string;
};

const IntroFormDoE = () => {
  const [colePriv, setColePriv] = useState('Publico');
  const [sexo, setSexo] = useState('masc');
  const [zona, setZona] = useState('urbana');
  const [clasesExtra, setClasesExtra] = useState('si');
  const [primeraLengua, setPrimeraLengua] = useState('es');
  const [email, setEmail] = useState('');
  const [accept, setAccept] = useState<boolean>(false);

  const authentication = useAuthentication();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  if ( isInAvataiSite())
    navigate(Paths.HOME, { replace: true });

  const currentUserQuery = useQuery<AxiosResponse<UserDto>, Error>(
    authenticationKeys.currentUser(),
    apiGetCurrentUser
  );

  disableBlurProtection();
  enableKeyCommands();

  const msg = "La información recolectada será utilizada para análisis estadístico de forma anónima." +
    " Posteriormente se enviarán los resultados de la prueba por correo electrónico." +
    " No se violentará su privacidad de ninguna forma.";

  const submitForm = useMutation<AxiosResponse<boolean>, Error, FormParams>(
    (params: FormParams) => {
      return submitUserFormD(colePriv, zona, clasesExtra, primeraLengua, sexo, email);
    },
    {
      onSuccess: () => {
        return queryClient.invalidateQueries(authenticationKeys.currentUser());
      },
    }
  );

  if (
    !currentUserQuery ||
    currentUserQuery.isLoading
  ) {
    return <Spinner />;
  }

  if (
    currentUserQuery.isError || (currentUserQuery.data &&
      currentUserQuery.data?.status !== 401 &&
      currentUserQuery.data.data &&
      currentUserQuery.data.data.email)
  )
    return <Navigate to={(Paths.HOME)}
                     state={{ from: location }} replace />;
  return (
    <Container maxW="container.md">
      <Formik
        initialValues={
          {
            privado: "",
            sexo: "",
            region: "",
            primeraLengua: "",
            clasesExtra: "",
            email: ""
          } as FormParams
        }
        onSubmit={(values: FormParams) =>
          submitForm.mutate(values)
        }
      >
        <Form>
          <Heading as='h4' size='lg'>Por favor seleccione las opciones según corresponda</Heading>
          <Field name="tipoCole">
            {({ field }: any) => (
              <FormControl isRequired>
                <FormLabel htmlFor="ID" paddingTop={4}>Colegio de procedencia:</FormLabel>
                <RadioGroup onChange={setColePriv} value={colePriv}>
                  <HStack spacing='24px'>
                    <Radio value='Publico'>Público</Radio>
                    <Radio value='Privado'>Privado</Radio>
                    <Radio value='SemiPrivado'>Semi privado</Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            )}
          </Field>
          <Field name="region">
            {({ field }: any) => (
              <FormControl isRequired>
                <FormLabel htmlFor="region" paddingTop={4}>Lugar de procedencia:</FormLabel>
                <RadioGroup onChange={setZona} value={zona}>
                  <HStack spacing='24px'>
                    <Radio value='urbana'>Zona urbana</Radio>
                    <Radio value='rural'>Zona rural</Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            )}
          </Field>
          <Field name="lang">
            {({ field }: any) => (
              <FormControl isRequired>
                <FormLabel htmlFor="lang" paddingTop={4}>Lengua materna:</FormLabel>
                <RadioGroup onChange={setPrimeraLengua} value={primeraLengua}>
                  <HStack spacing='24px'>
                    <Radio value='es'>Español</Radio>
                    <Radio value='eng'>Inglés</Radio>
                    <Radio value='otra'>Otra</Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            )}
          </Field>
          <Field name="extra">
            {({ field }: any) => (
              <FormControl isRequired>
                <FormLabel htmlFor="extra" paddingTop={4}>Formación extracurricular o adicional en inglés:</FormLabel>
                <RadioGroup onChange={setClasesExtra} value={clasesExtra}>
                  <HStack spacing='24px'>
                    <Radio value='si'>Sí</Radio>
                    <Radio value='no'>No</Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            )}
          </Field>
          <Field name="sexo">
            {({ field }: any) => (
              <FormControl isRequired>
                <FormLabel htmlFor="extra" paddingTop={4}>Sexo:</FormLabel>
                <RadioGroup onChange={setSexo} value={sexo}>
                  <HStack spacing='24px'>
                    <Radio value='masc'>Masculino</Radio>
                    <Radio value='fem'>Femenino</Radio>
                    <Radio value='otro'>Otro</Radio>
                  </HStack>
                </RadioGroup>
              </FormControl>
            )}
          </Field>
          <Field name="email">
            {({ field }: any) => (
              <FormControl isRequired>
                <FormLabel htmlFor="email" paddingTop={4}>Correo de la UCR</FormLabel>
                <Input {...field} id="name" type='email'
                       onChange={(e)=>setEmail(e.target.value?.toUpperCase())}
                       value={email}/>
              </FormControl>
            )}
          </Field>
          <Text paddingTop={4}>
            {msg}
          </Text>
          <Checkbox minW="fit-content"
                    onChange={async (e) => {
                      return setAccept(e.target.checked);
                    }}
                    defaultChecked={accept}>
            Aceptar
          </Checkbox>
          <Center>
            <Button
              mt={4}
              colorScheme="teal"
              type="submit"
              isLoading={authentication.loginMutation?.isLoading}
              disabled={!accept}
              _disabled={{
                cursor: 'default',
                opacity: '0.4',
              }}
            >
              Confirmar
            </Button>
          </Center>
        </Form>
      </Formik>
    </Container>
  );
};

export default IntroFormDoE;
